import React, { useState, useEffect } from "react";
import { Form, FormElement } from "../../types";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  UserIcon,
  CalendarIcon,
  ArchiveBoxIcon,
  EyeIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  CubeIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../../services/apiService";
import toast from "../../utils/toast";
import { useTranslation } from "react-i18next";

interface FormLibraryProps {
  onSelectForm?: (form: Form) => void;
  onCreateNew?: () => void;
  selectionMode?: boolean;
  selectedFormIds?: number[];
  multiSelect?: boolean;
}

export const FormLibrary: React.FC<FormLibraryProps> = ({
  onSelectForm,
  onCreateNew,
  selectionMode = false,
  selectedFormIds = [],
  multiSelect = false,
}) => {
  const { t } = useTranslation(["forms", "common"]);
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const [selectedForms, setSelectedForms] = useState<number[]>(selectedFormIds);

  // Load forms on mount
  useEffect(() => {
    loadForms();
  }, []);

  // Load data based on filters
  useEffect(() => {
    loadForms();
  }, [searchTerm, showOnlyMine]);

  const loadForms = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm || undefined,
        onlyMine: showOnlyMine || undefined,
      };

      const forms = await apiService.getAllForms(params);
      setForms(forms || []);
    } catch (error) {
      toast.error(t("forms:errorLoadingForms"));
    } finally {
      setLoading(false);
    }
  };

  const handleFormSelect = (form: Form) => {
    if (selectionMode) {
      if (multiSelect) {
        const newSelected = selectedForms.includes(form.id)
          ? selectedForms.filter((id) => id !== form.id)
          : [...selectedForms, form.id];
        setSelectedForms(newSelected);
      } else {
        setSelectedForms([form.id]);
        onSelectForm?.(form);
      }
    } else {
      onSelectForm?.(form);
    }
  };

  const handleDuplicateForm = async (
    formId: number,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    try {
      await apiService.duplicateForm(formId);
      toast.success(t("forms:formDuplicatedSuccessfully"));
      loadForms();
    } catch (error) {
      toast.error(t("forms:errorDuplicatingForm"));
    }
  };

  const handleArchiveForm = async (formId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!window.confirm(t("forms:confirmArchiveForm"))) return;

    try {
      const response = await fetch(`/api/forms/${formId}/archive`, {
        method: "PUT",
        credentials: "include",
      });

      if (response.ok) {
        toast.success(t("forms:formArchivedSuccessfully"));
        loadForms();
      } else {
        toast.error(t("forms:failedToArchiveForm"));
      }
    } catch (error) {
      toast.error(t("forms:errorArchivingForm"));
    }
  };

  const handleDeleteForm = async (formId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!window.confirm(t("forms:confirmDeleteFormPermanent"))) return;

    try {
      const response = await fetch(`/api/forms/${formId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success(t("forms:formDeletedSuccessfully"));
        loadForms();
      } else {
        toast.error(t("forms:failedToDeleteForm"));
      }
    } catch (error) {
      toast.error(t("forms:errorDeletingForm"));
    }
  };

  const getFormElementsSummary = (form: Form): string => {
    const elementCount = form.formStructure.elements.length;
    const elementTypes = form.formStructure.elements
      .map((el) => el.type)
      .reduce((acc, type) => {
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

    const typesSummary = Object.entries(elementTypes)
      .slice(0, 3)
      .map(([type, count]) => `${count} ${type}`)
      .join(", ");

    return `${elementCount} ${t("forms:elements")}: ${typesSummary}${
      Object.keys(elementTypes).length > 3 ? "..." : ""
    }`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">
          {t("forms:formLibrary")}
        </h2>
        {!selectionMode && (
          <button
            onClick={onCreateNew}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            {t("forms:createNewForm")}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Search */}
        <div className="relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={t("forms:searchForm")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* My Forms Toggle */}
        <div className="flex items-center">
          <input
            id="only-mine"
            type="checkbox"
            checked={showOnlyMine}
            onChange={(e) => setShowOnlyMine(e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="only-mine" className="ml-2 text-sm text-gray-700">
            {t("forms:onlyMyForms")}
          </label>
        </div>
      </div>

      {/* Forms Grid */}
      <div className="max-h-[calc(100vh-400px)] overflow-y-auto pr-2">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {forms.map((form) => (
              <div
                key={form.id}
                onClick={() => handleFormSelect(form)}
                className={`relative bg-white rounded-lg border-2 p-6 cursor-pointer transition-all duration-200 hover:shadow-lg ${
                  selectedForms.includes(form.id)
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                {/* Selection indicator */}
                {selectionMode && (
                  <div className="absolute top-3 right-3">
                    <div
                      className={`h-4 w-4 rounded-full border-2 ${
                        selectedForms.includes(form.id)
                          ? "bg-blue-600 border-blue-600"
                          : "border-gray-300"
                      }`}
                    >
                      {selectedForms.includes(form.id) && (
                        <div className="h-full w-full flex items-center justify-center">
                          <div className="h-2 w-2 bg-white rounded-full"></div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Form Header */}
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {form.title}
                  </h3>
                  {form.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {form.description}
                    </p>
                  )}
                </div>

                {/* Form Details */}
                <div className="space-y-2 mb-4">
                  <div className="text-xs text-gray-500">
                    {getFormElementsSummary(form)}
                  </div>

                  <div className="flex items-center text-xs text-gray-500">
                    <UserIcon className="h-3 w-3 mr-1" />
                    {form.createdByName || t("common:unknown")}
                    <span className="mx-2">•</span>
                    <CalendarIcon className="h-3 w-3 mr-1" />
                    {new Date(form.createdAt).toLocaleDateString()}
                  </div>

                  <div className="flex items-center text-xs text-gray-500">
                    <span>v{form.version}</span>
                    {form.isPublic && (
                      <>
                        <span className="mx-2">•</span>
                        <EyeIcon className="h-3 w-3 mr-1" />
                        {t("forms:public")}
                      </>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                {!selectionMode && (
                  <div className="absolute top-3 right-3 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => handleDuplicateForm(form.id, e)}
                      className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                      title={t("forms:duplicateForm")}
                    >
                      <DocumentDuplicateIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => handleArchiveForm(form.id, e)}
                      className="p-1 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded"
                      title={t("forms:archiveForm")}
                    >
                      <ArchiveBoxIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => handleDeleteForm(form.id, e)}
                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      title={t("forms:deleteForm")}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      {/* Empty State */}
      {forms.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <CubeIcon className="mx-auto h-12 w-12" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t("forms:noFormsFound")}
          </h3>
          <p className="text-gray-500">
            {searchTerm
              ? t("forms:tryAdjustingFilters")
              : t("forms:createFirstFormToStart")}
          </p>
        </div>
      )}

      {/* Selection Summary */}
      {selectionMode && multiSelect && selectedForms.length > 0 && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg">
          {selectedForms.length}{" "}
          {selectedForms.length !== 1 ? t("forms:forms") : t("forms:form")}{" "}
          {t("common:selected")}
        </div>
      )}
    </div>
  );
};
